/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React from "react";

import isInputCorrect from "src/utils/ResultValidationHelper";

import Card from "src/components/cards/Card.react";
import ResultDropdown from "src/components/dropdowns/Result.react";

import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/Table.react";
import ResultBadge from "src/components/badges/Result.react";
import AirGrip from "src/views/report/custom/AirGrip.react";
import TerminalBox from "src/views/report/custom/TerminalBox.react";
import Grease from "src/views/report/custom/Grease.react";
import PrintHeader from "src/components/print/PrintHeader.react";

/**
 * The Generator component
 *
 * @author Thais Watanabe
 */
const Generator = (props) => {
  const {
    turbineInspection,
    inspectionModelItems,
    inspectionModel,
    results,
    lastResults,
    lastTurbineInspections,
    openEdit,
    openObs,
    inspection,
  } = props;

  const places = ["U11", "V11", "W11", "U12", "V12", "W12"];

  const lastOneYear = lastTurbineInspections.find((obj) =>
    obj.inspection.type.startsWith("1年")
  );

  // 10-2
  const i10_2 = inspectionModelItems.filter((obj) => obj.name === "10-2")?.[0];
  const r10_2 = results.filter((obj) => obj.itemId === "10-2")?.[0];
  const last_r10_2 = lastResults?.filter(
    (obj) => obj.itemId === "10-2" && obj.turbineInspectionID === lastOneYear
  )?.[0];

  // 10-6
  const i10_6 = inspectionModelItems.filter((obj) => obj.name === "10-6")?.[0];
  const r10_6 = results.filter((obj) => obj.itemId === "10-6")?.[0];
  const last_r10_6 = lastResults?.filter(
    (obj) => obj.itemId === "10-6" && obj.turbineInspectionID === lastOneYear
  )?.[0];

  // 10-7
  const i10_7 = inspectionModelItems.filter((obj) => obj.name === "10-7")?.[0];
  const r10_7 = results.filter((obj) => obj.itemId === "10-7")?.[0];
  const last_r10_7_1 = lastResults?.filter((obj) => obj.itemId === "10-7")?.[0];
  const last_r10_7_2 = lastResults?.filter((obj) => obj.itemId === "10-7")?.[1];

  // 10-8
  const i10_8 = inspectionModelItems.filter((obj) => obj.name === "10-8")?.[0];
  const r10_8 = results.filter((obj) => obj.itemId === "10-8")?.[0];
  const last_r10_8_1 = lastResults?.filter((obj) => obj.itemId === "10-8")?.[0];
  const last_r10_8_2 = lastResults?.filter((obj) => obj.itemId === "10-8")?.[1];

  return (
    <>
      <Card title="エアギャップ測定結果表" className="print:mt-2">
        <div className="grid gap-x-4 grid-cols-2 print:gap-x-2">
          <AirGrip
            inspection={inspection}
            title={"今回"}
            inspectionModel={i10_2}
            result={r10_2}
            lastTurbineInspections={lastTurbineInspections}
            openEdit={openEdit}
            openObs={openObs}
            turbineInspection={turbineInspection}
            isPastInspection={false}
          />
          <AirGrip
            inspection={inspection}
            title={lastOneYear?.inspection?.name}
            inspectionModel={i10_2}
            result={last_r10_2}
            lastTurbineInspections={lastTurbineInspections}
            openEdit={openEdit}
            openObs={openObs}
            turbineInspection={turbineInspection}
            isPastInspection={true}
          />
        </div>
      </Card>

      <Card title="発電機ターミナルボックス 絶縁測定結果表" className="print:mt-2">
        <div className="flex flex-col gap-y-4 print:gap-y-2">
          <TerminalBox
            inspection={inspection}
            title={"今回"}
            inspectionModel={i10_6}
            result={r10_6}
            lastTurbineInspections={lastTurbineInspections}
            openEdit={openEdit}
            openObs={openObs}
            turbineInspection={turbineInspection}
            isPastInspection={false}
          />
          <TerminalBox
            inspection={inspection}
            title={lastOneYear?.inspection?.name}
            inspectionModel={i10_6}
            result={last_r10_6}
            lastTurbineInspections={lastTurbineInspections}
            openEdit={openEdit}
            openObs={openObs}
            turbineInspection={turbineInspection}
            isPastInspection={true}
          />
        </div>
      </Card>

      <Card title="温度センサ表示確認" className="pagebreak">
        <PrintHeader
          inspectionModel={inspectionModel}
          inspection={inspection}
          turbineInspection={turbineInspection}
        />

        <Table>
          <thead>
            <tr>
              <ColHeader id="10-7" className="w-28 text-center">
                <ResultDropdown
                  openEdit={openEdit}
                  openObs={openObs}
                  selectedItem={i10_7}
                  turbineInspection={turbineInspection}
                  selectedResult={r10_7}
                  lastTurbineInspections={lastTurbineInspections}
                  inspection={inspection}
                />
              </ColHeader>
              <ColHeader className="text-center" colSpan={6}>
                Stator 1 Tempearature
              </ColHeader>
              <ColHeader className="text-center" colSpan={6}>
                Stator 2 Tempearature
              </ColHeader>
            </tr>
            <tr>
              <ColHeader className="text-center">箇所</ColHeader>
              {[...Array(6)].map((x, i) => (
                <ColHeader className="text-center" key={i}>
                  {i + 1}
                </ColHeader>
              ))}
              {[...Array(6)].map((x, i) => (
                <ColHeader className="text-center" key={i}>
                  {i + 1}
                </ColHeader>
              ))}
            </tr>
          </thead>
          <TableBody>
            <tr>
              <Col className="text-center">今回 判定</Col>
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={r10_7?.childrenResults[i] ?? "-"}
                    unit={i10_7?.inspectionModelItemChildren.items?.[i].unit}
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      r10_7?.childrenResults[i] ?? "-",
                      i
                    )}
                  />
                </Col>
              ))}
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={r10_7?.childrenResults[6 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[6 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      r10_7?.childrenResults[6 + i] ?? "-",
                      6 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[0]?.inspection?.name}
              </Col>
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_1?.childrenResults[i] ?? "-"}
                    unit={i10_7?.inspectionModelItemChildren.items?.[i].unit}
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_1?.childrenResults[i] ?? "-",
                      i
                    )}
                  />
                </Col>
              ))}
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_1?.childrenResults[6 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[6 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_1?.childrenResults[6 + i] ?? "-",
                      6 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[1]?.inspection?.name}
              </Col>
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_2?.childrenResults[i] ?? "-"}
                    unit={i10_7?.inspectionModelItemChildren.items?.[i].unit}
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_2?.childrenResults[i] ?? "-",
                      i
                    )}
                  />
                </Col>
              ))}
              {[...Array(6)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_2?.childrenResults[6 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[6 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_2?.childrenResults[6 + i] ?? "-",
                      6 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
          </TableBody>
        </Table>

        <Table className="print:mt-2">
          <thead>
            <tr>
              <ColHeader className="w-28 text-center">
                <ResultDropdown
                  openEdit={openEdit}
                  openObs={openObs}
                  selectedItem={i10_7}
                  turbineInspection={turbineInspection}
                  selectedResult={r10_7}
                  lastTurbineInspections={lastTurbineInspections}
                  inspection={inspection}
                />
              </ColHeader>
              <ColHeader className="text-center" colSpan={2}>
                Air Temperature
              </ColHeader>
              <ColHeader className="text-center" colSpan={2}>
                Bearing Temperature
              </ColHeader>
            </tr>
            <tr>
              <ColHeader className="text-center">箇所</ColHeader>
              {[...Array(2)].map((x, i) => (
                <ColHeader className="text-center" key={i}>
                  {i + 1}
                </ColHeader>
              ))}
              {[...Array(2)].map((x, i) => (
                <ColHeader className="text-center" key={i}>
                  {i + 1}
                </ColHeader>
              ))}
            </tr>
          </thead>
          <TableBody>
            <tr>
              <Col className="text-center">今回 判定</Col>
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={r10_7?.childrenResults[12 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[12 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      r10_7?.childrenResults[12 + i] ?? "-",
                      12 + i
                    )}
                  />
                </Col>
              ))}
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={r10_7?.childrenResults[14 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[14 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      r10_7?.childrenResults[14 + i] ?? "-",
                      14 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[0]?.inspection?.name}
              </Col>
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_1?.childrenResults[12 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[12 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_1?.childrenResults[12 + i] ?? "-",
                      12 + i
                    )}
                  />
                </Col>
              ))}
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_1?.childrenResults[14 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[14 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_1?.childrenResults[14 + i] ?? "-",
                      14 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[1]?.inspection?.name}
              </Col>
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_2?.childrenResults[12 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[12 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_2?.childrenResults[12 + i] ?? "-",
                      12 + i
                    )}
                  />
                </Col>
              ))}
              {[...Array(2)].map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_2?.childrenResults[14 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[14 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_2?.childrenResults[14 + i] ?? "-",
                      14 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
          </TableBody>
        </Table>

        <Table className="print:mt-2">
          <thead>
            <tr>
              <ColHeader className="w-28 text-center">
                <ResultDropdown
                  openEdit={openEdit}
                  openObs={openObs}
                  selectedItem={i10_7}
                  turbineInspection={turbineInspection}
                  selectedResult={r10_7}
                  lastTurbineInspections={lastTurbineInspections}
                  inspection={inspection}
                />
              </ColHeader>
              <ColHeader className="text-center" colSpan={3}>
                Crimp Temp
              </ColHeader>
              <ColHeader className="text-center" colSpan={3}>
                Crimp Temp
              </ColHeader>
            </tr>
            <tr>
              <ColHeader className="text-center">箇所</ColHeader>
              {places.map((x, i) => (
                <ColHeader className="text-center" key={i}>
                  {places[i]}
                </ColHeader>
              ))}
            </tr>
          </thead>
          <TableBody>
            <tr>
              <Col className="text-center">今回 判定</Col>
              {places.map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={r10_7?.childrenResults[16 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[16 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      r10_7?.childrenResults[16 + i] ?? "-",
                      16 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[0]?.inspection?.name}
              </Col>
              {places.map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_1?.childrenResults[16 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[16 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_1?.childrenResults[16 + i] ?? "-",
                      16 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
            <tr>
              <Col className="text-center">
                {lastTurbineInspections?.[1]?.inspection?.name}
              </Col>
              {places.map((x, i) => (
                <Col className="text-center" key={i}>
                  <ResultBadge
                    result={last_r10_7_2?.childrenResults[16 + i] ?? "-"}
                    unit={
                      i10_7?.inspectionModelItemChildren.items?.[16 + i].unit
                    }
                    isInputCorrect={isInputCorrect(
                      i10_7?.name,
                      last_r10_7_2?.childrenResults[16 + i] ?? "-",
                      16 + i
                    )}
                  />
                </Col>
              ))}
            </tr>
          </TableBody>
        </Table>
      </Card>

      <Card className="print:mt-2">
        <Grease
          inspection={inspection}
          title={"メインベアリング自動潤滑装置　グリース記録表"}
          inspectionModel={i10_8}
          result={r10_8}
          lastResult1={last_r10_8_1}
          lastResult2={last_r10_8_2}
          lastTurbineInspections={lastTurbineInspections}
          openEdit={openEdit}
          openObs={openObs}
          turbineInspection={turbineInspection}
        />
      </Card>
    </>
  );
};

export default Generator;
